import { useSearchParams } from "react-router";

import { TypedGridColDef } from "@shared";
import { formatParticipantStreetLine } from "@shared";
import { participantTypeLabels } from "@shared";
import { useQuery } from "@tanstack/react-query";

import { ProceedingParticipantSearchResult, proceedingParticipantsApi } from "@/api";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { createColumns } from "@/common/components/table/DataTable.utils";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { formatTimestamp } from "@/common/utils/formatTimestamp";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { ParticipantsTableWidth } from "../ProceedingParticipants.utils";

const SEARCH_PARAM = "searchText";
const SHOW_INACTIVE_PARAM = "showInactive";

export function useProceedingParticipants() {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchText = searchParams.get(SEARCH_PARAM) ?? undefined;
  const showInactive = searchParams.get(SHOW_INACTIVE_PARAM) === "true";

  const { data, isLoading } = useQuery({
    queryKey: [QueryKey.ProceedingParticipants, searchText, showInactive],
    queryFn: () =>
      proceedingParticipantsApi.searchProceedingParticipants({ searchText, includeInactive: showInactive }),
    meta: { loadingScope: "pageContent" },
  });

  const typeLabels = participantTypeLabels(t);

  const inactiveColor = (row: ProceedingParticipantSearchResult) => (row.isActive ? undefined : "text.disabled");

  const columnConfigurations = createColumns<ProceedingParticipantSearchResult>([
    {
      field: "name",
      headerName: t("proceedingParticipants.table.name"),
      flex: 1,
      renderCell: (params) => {
        const row = params.row as ProceedingParticipantSearchResult;
        return <EllipsisText value={params.value as string} variant="body2" color={inactiveColor(row)} />;
      },
    },
    {
      field: "type",
      headerName: t("proceedingParticipants.table.type"),
      flex: 1,
      maxWidth: ParticipantsTableWidth.type,
      valueGetter: (_, row) => typeLabels[(row as ProceedingParticipantSearchResult).type] ?? "",
      renderCell: (params) => {
        const row = params.row as ProceedingParticipantSearchResult;
        return <EllipsisText value={params.value as string} variant="body2" color={inactiveColor(row)} />;
      },
    },
    {
      field: "street",
      headerName: t("proceedingParticipants.table.address"),
      flex: 1,
      maxWidth: ParticipantsTableWidth.street,
      valueGetter: (_, row) => {
        const participant = row as ProceedingParticipantSearchResult;
        return formatParticipantStreetLine(participant.street, participant.houseNumber);
      },
      renderCell: (params) => {
        const row = params.row as ProceedingParticipantSearchResult;
        return <EllipsisText value={params.value as string} variant="body2" color={inactiveColor(row)} />;
      },
    },
    {
      field: "zipCode",
      headerName: t("proceedingParticipants.table.zipCode"),
      flex: 1,
      maxWidth: ParticipantsTableWidth.zipCode,
      renderCell: (params) => {
        const row = params.row as ProceedingParticipantSearchResult;
        return (
          <EllipsisText value={(params.value as string | null) ?? ""} variant="body2" color={inactiveColor(row)} />
        );
      },
    },
    {
      field: "city",
      headerName: t("proceedingParticipants.table.city"),
      flex: 1,
      maxWidth: ParticipantsTableWidth.city,
      renderCell: (params) => {
        const row = params.row as ProceedingParticipantSearchResult;
        return (
          <EllipsisText value={(params.value as string | null) ?? ""} variant="body2" color={inactiveColor(row)} />
        );
      },
    },
    ...(showInactive
      ? ([
          {
            field: "deactivatedAtUtc",
            headerName: t("proceedingParticipants.table.inactiveSince"),
            flex: 1,
            maxWidth: ParticipantsTableWidth.deactivatedAtUtc,
            renderCell: (params) => {
              const row = params.row as ProceedingParticipantSearchResult;
              const value = row.deactivatedAtUtc ? formatTimestamp(row.deactivatedAtUtc, t, language) : "–";
              return <EllipsisText value={value} variant="body2" color={inactiveColor(row)} />;
            },
          },
        ] as Array<TypedGridColDef<ProceedingParticipantSearchResult>>)
      : []),
  ]);

  function setSearchText(value: string | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(SEARCH_PARAM, value);
      } else {
        next.delete(SEARCH_PARAM);
      }
      return next;
    });
  }

  function setShowInactive(value: boolean) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(SHOW_INACTIVE_PARAM, "true");
      } else {
        next.delete(SHOW_INACTIVE_PARAM);
      }
      return next;
    });
  }

  return {
    participants: data?.data ?? [],
    hasAdditionalParticipants: data?.hasMoreItems ?? false,
    isLoading,
    columnConfigurations,
    searchText: searchText ?? "",
    setSearchText,
    showInactive,
    setShowInactive,
  };
}
